import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

import { AUTH_SERVER_URL, AUTH_SERVER_FRONTEND_KEY, API_URL, CONGRESS_ID } from '@/config'
import { publicPath } from '@/../vue.config'
import router from '@/router'
import initialize from '@/lib/initialize'

import { login } from '@/views/login/login.store'
import { schedule } from '@/views/schedule/schedule.store'
import { sessions } from '@/views/sessions/sessions.store'
import { session } from '@/views/session/session.store'
import { session_edit } from '@/views/session/session_edit.store'
import { session_persons } from '@/views/session/session_persons.store'
import { session_presentations } from '@/views/session/session_presentations.store'
import { session_participations } from '@/views/session/session_participations.store'
import { session_sessions } from '@/views/session/session_sessions.store'
import { presentations } from '@/views/presentations/presentations.store'
import { presentation } from '@/views/presentation/presentation.store'
import { presentation_edit } from '@/views/presentation/presentation_edit.store'
import { presentation_persons } from '@/views/presentation/presentation_persons.store'
import { presentation_participations } from '@/views/presentation/presentation_participations.store'
import { persons } from '@/views/persons/persons.store'
import { person } from '@/views/person/person.store'
import { person_edit } from '@/views/person/person_edit.store'
import { person_participations } from '@/views/person/person_participations.store'
import { person_fundings } from '@/views/person/person_fundings.store'
import { meetings } from '@/views/meetings/meetings.store'
import { meeting } from '@/views/meeting/meeting.store'
import { meeting_edit } from '@/views/meeting/meeting_edit.store'
import { mailings } from '@/views/mailings/mailings.store'
import { mailing } from '@/views/mailing/mailing.store'
import { mailing_edit } from '@/views/mailing/mailing_edit.store'
import { mailing_recipients } from '@/views/mailing/mailing_recipients.store'
import { mailing_template } from '@/views/mailing/mailing_template.store'
import { mailing_attachments } from '@/views/mailing/mailing_attachments.store'
import { mailing_delivery } from '@/views/mailing/mailing_delivery.store'
import { mailing_tracking } from '@/views/mailing/mailing_tracking.store'
import { rooms } from '@/views/rooms/rooms.store'
import { room } from '@/views/room/room.store'
import { room_edit } from '@/views/room/room_edit.store'
import { reports } from '@/views/reports/reports.store'
import { reports_individual } from '@/views/reports/reports_individual.store'

export default new Vuex.Store({

  modules: {
    login,
    schedule,
    sessions,
    session,
    session_edit,
    session_persons,
    session_presentations,
    session_participations,
    session_sessions,
    presentations,
    presentation,
    presentation_edit,
    presentation_persons,
    presentation_participations,
    persons,
    person,
    person_edit,
    person_participations,
    person_fundings,
    meetings,
    meeting,
    meeting_edit,
    mailings,
    mailing,
    mailing_edit,
    mailing_recipients,
    mailing_template,
    mailing_attachments,
    mailing_delivery,
    mailing_tracking,
    rooms,
    room,
    room_edit,
    reports,
    reports_individual,
  },

  state: {
    app: 'planner',
    bar: true,
    progress: false,
    cursor: 'auto',
    key: null,
    language: '',
    settings: {},
    authorized: {},
    routes: {},
    user: {},
    _: {},
    modal: { status: false, component: '', class: 'small' },
    tooltip: { active: false, top: -200, left: -200, right: false, content: '' },
    details: { model: '', id: null },
    manager: { abstract_id: null },
    conflicts: { model: '', type: '', id: null },
    copy: { model: '', id: null },
    clear: { model: '', id: null, items: '', ids: [] },
    del: { model: '', id: null, action: { type: '', path: '' } },
    massdel: { model: '', ids: null, action: { type: '', path: '' } },
    upload: { model: '' },
    response: { status: false, type: '', message: '', errors: {} },
    history: [],
  },

  getters: {
    bar: state => state.bar,
    progress: state => state.progress,
    cursor: state => state.cursor,
    key: state => state.key,
    language: state => state.language,
    settings: state => state.settings,
    authorized: state => state.authorized,
    routes: state => state.routes,
    user: state => state.user,
    _: state => state._,
    modal: state => state.modal,
    tooltip: state => state.tooltip,
    details: state => state.details,
    manager: state => state.manager,
    conflicts: state => state.conflicts,
    copy: state => state.copy,
    clear: state => state.clear,
    del: state => state.del,
    massdel: state => state.massdel,
    upload: state => state.upload,
    response: state => state.response,
    history: state => state.history.slice().reverse(),
  },

  mutations: {
    setProgress(state, progress) { state.progress = progress },
    setCursor(state, cursor) { state.cursor = cursor },
    setKey(state, key) { state.key = key },
    setLanguage(state, language) { state.language = language },
    setSettings(state, settings) { state.settings = settings },
    setAuthorized(state, authorized) { state.authorized = authorized },
    setRoutes(state, routes) { state.routes = routes },
    setUser(state, user) { state.user = user },
    setWordings(state, wordings) { state._ = wordings },
    setModal(state, modal) { state.modal = modal },
    setTooltip(state, tooltip) { state.tooltip = tooltip },
    setDetails(state, details) { state.details = details },
    setManager(state, manager) { state.manager = manager },
    setConflicts(state, conflicts) { state.conflicts = conflicts },
    setCopy(state, copy) { state.copy = copy },
    setClear(state, clear) { state.clear = clear },
    setDelete(state, del) { state.del = del },
    setMassDelete(state, massdel) { state.massdel = massdel },
    setUpload(state, upload) { state.upload = upload },
    setResponse(state, response) {
      state.response.status = response.status || false;
      state.response.type = response.type || '';
      state.response.message = response.message || '';
      state.response.errors = response.errors || {};
    },
    clearResponse(state) { 
      state.response = { status: false, type: '', message: '', errors: {} }
    },
    setHistory(state, history) {
      if (state.history.length >= 20) state.history.shift();
      state.history.push(history); 
    },
    closeModal(state) { state.modal = { status: false, component: '' } },
  },
  
  actions: {

    getSettings: () => Vue.axios.get(API_URL + '/settings'),
    getAuthorized: () => Vue.axios.get(API_URL + '/authorizations'),
    getRoutes: () => Vue.axios.get(API_URL + '/routes'),
    getUser: () => Vue.axios.get(API_URL + '/user'),

    validateUser: (context, key) => Vue.axios.get(AUTH_SERVER_URL + 'api/role/isAllowed/planner-user/' + CONGRESS_ID, {
      headers: {
        'x-api-key': key,
      }
    }),

    getWordings: (state) => Vue.axios.get(API_URL + '/wordings', {
      params: {
        language_id: state.getters.language,
      }
    }),

    authenticateUser() {
      window.location = AUTH_SERVER_URL + 'application/' + AUTH_SERVER_FRONTEND_KEY;
    },

    async loginUser({ state, commit, dispatch }, params) {
      await dispatch('validateUser', params.key);
      localStorage.setItem('x-api-key', params.key);
      Vue.axios.defaults.headers.common['x-api-key'] = params.key;
      commit('setKey', params.key, { root: true });
      window.history.pushState({}, document.title, publicPath);
      await initialize();
      router.push(state.routes.start);
    },

    async logoutUser({ commit }) {
      await Vue.axios.get(API_URL + '/logout');
      localStorage.removeItem('x-api-key');
      Vue.axios.defaults.headers.common['x-api-key'] = null;
      commit('setKey', null, { root: true });
      commit('setProgress', false, { root: true });
      router.push('/login');
    },

    toggleBar({ state, dispatch }) {
      state.bar = !state.bar
      dispatch('updateTooltip', { content: state.bar ? 'sidebar_hide' : 'sidebar_show' })
    },

    async changeLanguage({ rootState, commit, dispatch }, language) {
      commit('setLanguage', language);
      const wordings = await dispatch('getWordings');
      commit('setProgress', false, { root: true });
      commit('setWordings', wordings.data);
      if (router.currentRoute.path.match(/^\/persons$/)) {
        rootState.persons.options.country_code.options = rootState.persons.options.country_code.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/session\/[0-9]+\/persons$/)) {
        rootState.session_persons.options.country_code.options = rootState.session_persons.options.country_code.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/presentation\/[0-9]+\/persons$/)) {
        rootState.presentation_persons.options.country_code.options = rootState.presentation_persons.options.country_code.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/mailing\/[0-9]+\/recipients$/)) {
        rootState.mailing_recipients.area['App\\Models\\Person'].filteroptions.country_code.options = rootState.mailing_recipients.area['App\\Models\\Person'].filteroptions.country_code.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/reports\/individual$/)) {
        rootState.reports_individual.area['App\\Models\\Person'].filteroptions.country_code.options = rootState.reports_individual.area['App\\Models\\Person'].filteroptions.country_code.options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/person\/[0-9]+\/edit$/)) {
        let countries = rootState.person_edit.entities.findIndex(entity => entity.column === 'country_code');
        rootState.person_edit.entities[countries].options = rootState.person_edit.entities[countries].options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/session\/[0-9]+\/persons$/)) {
        let countries = rootState.session_persons.entities.findIndex(entity => entity.column === 'country_code');
        rootState.session_persons.entities[countries].options = rootState.session_persons.entities[countries].options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
      if (router.currentRoute.path.match(/^\/presentation\/[0-9]+\/persons$/)) {
        let countries = rootState.presentation_persons.entities.findIndex(entity => entity.column === 'country_code');
        rootState.presentation_persons.entities[countries].options = rootState.presentation_persons.entities[countries].options.sort((a, b) => rootState._[a.name].localeCompare(rootState._[b.name]));
      }
    },

    showTooltip({ state }, tooltip) {
      const rect = tooltip.event.target.getBoundingClientRect()
      state.tooltip = {
        active: true,
        top: rect.y,
        left: rect.x,
        right: tooltip.right || false,
        width: rect.width,
        content: tooltip.content
      }
    },

    updateTooltip({ state }, tooltip) {
      state.tooltip = {
        right: true,
        content: tooltip.content
      }
    },

    hideTooltip({ state }) { 
      state.tooltip = {
        active: false,
        top: -200,
        left: -200,
        right: false,
        width: 0,
        content: ''
      }
    },

  }
})